import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, Hash, Mail } from 'lucide-react';
import { auth } from '../services/api';

const inputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '11px 42px 11px 14px',
  background: 'var(--nx-surface-2)',
  border: `1px solid ${hasError ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)'}`,
  borderRadius: 10,
  fontSize: 14,
  color: 'var(--nx-text-primary)',
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color 150ms',
});

const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [identifier, setIdentifier] = useState('');
  const [societyCode, setSocietyCode] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [codeError, setCodeError] = useState('');

  const isUsername = identifier.length > 0 && !identifier.includes('@');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let valid = true;

    if (!identifier.trim()) {
      setIdentifierError('Email or username is required');
      valid = false;
    } else {
      setIdentifierError('');
    }

    if (isUsername && !societyCode.trim()) {
      setCodeError('Society code is required when using a username');
      valid = false;
    } else {
      setCodeError('');
    }

    if (!valid) return;

    setIsLoading(true);
    setSubmitError(null);
    try {
      await auth.requestPasswordReset(identifier.trim(), societyCode.trim() || undefined);
      setSent(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; detail?: string } } };
      const msg = e.response?.data?.message ?? e.response?.data?.detail;
      setSubmitError(msg ?? 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nx-navy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(26,108,246,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />
      {/* Radial glow */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '500px',
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(26,108,246,0.14) 0%, rgba(109,40,217,0.07) 40%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Card */}
      <div
        className="nx-anim-fade-up"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,108,246,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ height: 3, background: 'linear-gradient(90deg, #1A6CF6, #6D28D9)' }} />

        <div style={{ padding: '36px 32px' }}>
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff' }}>
                Nx
              </span>
            </div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: 'var(--nx-text-primary)', letterSpacing: '-0.02em' }}>
                NEXORA
              </div>
              <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', letterSpacing: '0.08em' }}>
                Pentagon Analytics
              </div>
            </div>
          </div>

          {sent ? (
            /* ── Success ── */
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, marginBottom: 28 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={24} style={{ color: '#10B981' }} />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 8 }}>
                    Check your email
                  </h1>
                  <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>
                    If that account exists, a password reset link has been sent to the registered email. It expires in 15 minutes.
                  </p>
                </div>
              </div>
              <Link to="/login" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          ) : (
            /* ── Form ── */
            <>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 6 }}>
                  Forgot password?
                </h1>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
                  Enter your email or username. If you use a username, your society code is also required.
                </p>
              </div>

              {submitError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, marginBottom: 20 }}>
                  <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#EF4444' }}>{submitError}</span>
                </div>
              )}

              <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} noValidate>

                {/* Identifier */}
                <div>
                  <label htmlFor="identifier" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                    EMAIL OR USERNAME
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="identifier"
                      type="text"
                      autoComplete="username"
                      autoFocus
                      placeholder="admin@nexora.io or swetha"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      style={inputStyle(!!identifierError)}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = identifierError ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)')}
                    />
                    <Mail size={15} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
                  </div>
                  {identifierError && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{identifierError}</p>}
                </div>

                {/* Society code — required for residents, not needed for society
                    admin/staff email resets. Always shown so residents whose username
                    happens to be their email still have a place to disambiguate. */}
                <div style={{ padding: '14px', background: 'rgba(26,108,246,0.04)', border: '1px solid rgba(26,108,246,0.14)', borderRadius: 10 }}>
                  <label htmlFor="societyCode" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                    SOCIETY CODE {!isUsername && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>(residents only)</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="societyCode"
                      type="text"
                      autoComplete="off"
                      placeholder="e.g. NEX0001"
                      value={societyCode}
                      onChange={(e) => setSocietyCode(e.target.value.toUpperCase())}
                      style={inputStyle(!!codeError)}
                      onFocus={(e) => (e.currentTarget.style.borderColor = '#1A6CF6')}
                      onBlur={(e) => (e.currentTarget.style.borderColor = codeError ? 'rgba(239,68,68,0.5)' : 'var(--nx-border)')}
                    />
                    <Hash size={15} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
                  </div>
                  {codeError
                    ? <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{codeError}</p>
                    : <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>
                        {isUsername
                          ? 'Find your society code on the notice board or ask your secretary.'
                          : 'If you’re a resident using your email as your username, enter your society code too.'}
                      </p>
                  }
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="nx-btn nx-btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14, fontWeight: 600, marginTop: 4, opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                >
                  {isLoading ? (
                    <>
                      <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                      Sending link...
                    </>
                  ) : 'Send reset link'}
                </button>
              </form>

              <p style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: 'var(--nx-text-muted)' }}>
                <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
                  <ArrowLeft size={12} /> Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, marginTop: 24, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        © {new Date().getFullYear()} Pentagon Analytics. All rights reserved.
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
